import { CustomerInterface } from '@app/models/business';
import { getPaginatedCustomers } from '@app/services/customers';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { updateFormData, resetForm } from '@app/redux/features/campaignForm';
import { ListInterface } from '@app/models/util';
import { getLists, getPaginatedLists } from '@app/services/list';
import { getTag } from '@app/services/tag';

export const useCreateCampaign = (form: UseFormReturn) => {
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [listQuery, setListQuery] = useState<{
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: 10,
  });
  const listQueryText = useRef<string | undefined>(undefined);
  const customerQueryText = useRef<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const formData = useAppSelector((state) => state.campaignForm.formData);
  const [customerQuery, setCustomerQuery] = useState<{
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: 10,
  });
  const [customerFilters, setCustomerFilters] = useState<{
    channelType?: string;
    status?: string;
    ratings?: string;
    lists?: string;
    tags?: string;
    unsubscribed?: boolean;
  }>({
    channelType: undefined,
    status: undefined,
    ratings: undefined,
    lists: undefined,
    tags: undefined,
    unsubscribed: false,
  });

  const [listSortBy, setListSortBy] = useState<string>('createdAt');
  const [listSortOrder, setListSortOrder] = useState<'asc' | 'desc'>('desc');
  const [customerSortBy, setCustomerSortBy] = useState<string>('createdAt');
  const [customerSortOrder, setCustomerSortOrder] = useState<'asc' | 'desc'>('desc');

  const {
    data: customers,
    isLoading,
    refetch: refetchCustomers,
  } = useQuery({
    queryKey: ['customers', customerQueryText.current, customerQuery, customerFilters, customerSortBy, customerSortOrder],
    queryFn: () => {
      const limit = customerQuery?.limit ?? 10;
      const page = customerQuery?.page ?? 1;
      const skip = (page - 1) * limit;

      return getPaginatedCustomers({
        text: customerQueryText.current,
        pagination: { limit, page, skip },
        sort: { field: customerSortBy, order: customerSortOrder.toUpperCase() as 'ASC' | 'DESC' },
        ...customerFilters
      });
    },
  });

  const {
    data: listsData,
    isLoading: isLoadingLists,
    refetch,
  } = useQuery<{ lists: ListInterface[]; count: number }>({
    queryKey: ['lists-paginated', listQuery.page, listQuery.limit, listSortBy, listSortOrder],
    queryFn: () => {
      const limit = listQuery?.limit ?? 10;
      const page = listQuery?.page ?? 1;
      const skip = (page - 1) * limit;

      return getPaginatedLists({
        text: listQueryText.current,
        pagination: { limit, page, skip },
        sortBy: listSortBy,
        sortOrder: listSortOrder,
      })
        .then((res) => res?.data)
        .catch((err) => err);
    },
  });

    const { data: listsAndTags } = useQuery({
      queryKey: ['all-lists-and-tags'],
      queryFn: async () =>
        await Promise.all([getLists(), getTag()])
          .then((responses) => {
            return {
              lists: responses?.[0]?.data?.lists,
              tags: responses?.[1]?.data?.tags,
            };
          })
          .catch((err) => err),
    });

  // Load saved form data when the component mounts
  useEffect(() => {
    // Only set values that exist in formData and aren't already set in the form
    if (formData) {
      Object.keys(formData).forEach((key) => {
        const currentValue = form.getValues(key);
        if (formData[key] !== undefined && !currentValue) {
          form.setValue(key, formData[key], { shouldDirty: true });
        }
      });
    }
  }, [form, formData]);

  // Save form data when it changes
  const saveFormData = useCallback(() => {
    const values = form.getValues();
    dispatch(updateFormData(values));
  }, [form, dispatch]);

  // Add a function to completely reset the form
  const resetCampaignForm = useCallback(() => {
    form.reset();
    dispatch(resetForm());
    localStorage.removeItem('campaignFormData');
  }, [form, dispatch]);

  const handleCustomersModal = () => {
    // Save current form state before opening/closing modal
    saveFormData();
    setShowCustomerModal(!showCustomerModal);
  };

  const handleListsModal = () => {
    // Save current form state before opening/closing modal
    saveFormData();
    setShowListModal(!showListModal);
  };

  const handleCustomerSelection = useCallback(
    (customerId?: string) => {
      if (!customerId) return;
      const selectedCustomers = form.watch('selectedCustomers') ?? [];

      if (customerId === 'all') {
        const allPageCustomers = customers?.data?.customers ?? [];
        const customersId = allPageCustomers.map(
          (cus: CustomerInterface) => cus.id
        );

        const selectedId = selectedCustomers.map(
          (cus: CustomerInterface) => cus.id
        );

        if (customersId.every((id: string) => selectedId.includes(id))) {
          form.setValue('selectedCustomers', []);
          saveFormData();
          return;
        } else {
          const newSelected = [
            ...selectedCustomers,
            ...(customers?.data?.customers?.filter(
              (cus: CustomerInterface) => !selectedId.includes(cus.id)
            ) ?? []),
          ];

          form.setValue('selectedCustomers', newSelected);
          saveFormData();
        }
        return;
      }

      if (
        selectedCustomers.some(
          (cus: CustomerInterface) => cus.id === customerId
        )
      ) {
        const updatedList = selectedCustomers.filter(
          (cus: CustomerInterface) => cus.id !== customerId
        );

        form.setValue('selectedCustomers', updatedList);
        saveFormData();
      } else {
        const customer = customers?.data?.customers?.find(
          (cus: CustomerInterface) => cus.id === customerId
        );
        if (customer) {
          const updatedList = [...selectedCustomers, customer];

          form.setValue('selectedCustomers', updatedList);
          saveFormData();
        }
      }
    },
    [customers, form, saveFormData]
  );

  const handleListsNextPage = () => {
    setListQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  };

  const handleListsPrevPage = () => {
    setListQuery((prev) => ({
      ...prev,
      page: prev.page === 0 ? 0 : prev.page - 1,
    }));
  };

  const handleListsLimit = (value: any) => {
    setListQuery((prev) => ({ ...prev, limit: value }));
  };

  const handleFilterLists = (value: string) => {
    listQueryText.current = value;
    setListQuery((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const handleFilterCustomers = (value: string) => {
    customerQueryText.current = value;
    setCustomerQuery((prev) => ({ ...prev, page: 1 }));
    refetchCustomers();
  };

  const handleCustomersNextPage = () => {
    setCustomerQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  };

  const handleCustomersPrevPage = () => {
    setCustomerQuery((prev) => ({
      ...prev,
      page: prev.page === 0 ? 0 : prev.page - 1,
    }));
  };
  const handleCustomersLimit = (value: any) => {
    setCustomerQuery((prev) => ({ ...prev, limit: value }));
  };

  const handleSortingTable = (field: string) => {
    if (listSortBy === field) {
      setListSortOrder(listSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setListSortBy(field);
      setListSortOrder('asc');
    }
  };

  const handleCustomerSortingTable = (field: string, order: 'ASC' | 'DESC') => {
    setCustomerSortBy(field);
    setCustomerSortOrder(order.toLowerCase() as 'asc' | 'desc');
  };

  useEffect(() => {
    if (listsData) {
      refetch();
    }
  }, [listQuery.limit, listQuery.page, listSortBy, listSortOrder]);

  useEffect(() => {
    if (customers) {
      refetchCustomers();
    }
  }, [customerQuery.limit, customerQuery.page]);

  useEffect(() => {
    if (customerFilters) {
      refetchCustomers();
    }
  }, [customerFilters]);
  


  return {
    customers,
    isLoading,
    handleCustomersModal,
    showCustomerModal,
    handleCustomerSelection,
    saveFormData,
    resetCampaignForm, // Export the reset function
    handleListsModal,
    showListModal,
    listsData,
    isLoadingLists,
    handleListsNextPage,
    handleListsPrevPage,
    handleListsLimit,
    listQuery,
    refetch,
    handleFilterLists,
    handleCustomersNextPage,
    handleCustomersPrevPage,
    handleCustomersLimit,
    customerQuery,
    refetchCustomers,
    setCustomerFilters,
    listsAndTags,
    handleFilterCustomers,
    handleSortingTable,
    handleCustomerSortingTable,
    listSortBy,
    listSortOrder,
  };
};
